import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, GripVertical, Link as LinkIcon, ChevronDown, ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface ResourceLink {
  title: string;
  description: string;
  url: string;
}

export interface ResourceSection {
  title: string;
  tooltip?: string;
  links: ResourceLink[];
}

export interface PathResources {
  title: string;
  description: string;
  sections: ResourceSection[];
}

interface PathResourcesEditorProps {
  resources: PathResources | null;
  onChange: (resources: PathResources | null) => void;
  disabled: boolean;
}

export function PathResourcesEditor({ resources, onChange, disabled }: PathResourcesEditorProps) {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  const data: PathResources = resources || { title: "", description: "", sections: [] };

  const update = (patch: Partial<PathResources>) => {
    onChange({ ...data, ...patch });
  };

  const toggleSection = (idx: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const addSection = () => {
    update({ sections: [...data.sections, { title: "", links: [] }] });
  };

  const updateSection = (idx: number, patch: Partial<ResourceSection>) => {
    const sections = [...data.sections];
    sections[idx] = { ...sections[idx], ...patch };
    update({ sections });
  };

  const removeSection = (idx: number) => {
    update({ sections: data.sections.filter((_, i) => i !== idx) });
  };

  const addLink = (sectionIdx: number) => {
    const sections = [...data.sections];
    sections[sectionIdx] = {
      ...sections[sectionIdx],
      links: [...sections[sectionIdx].links, { title: "", description: "", url: "" }],
    };
    update({ sections });
  };

  const updateLink = (sectionIdx: number, linkIdx: number, patch: Partial<ResourceLink>) => {
    const sections = [...data.sections];
    const links = [...sections[sectionIdx].links];
    links[linkIdx] = { ...links[linkIdx], ...patch };
    sections[sectionIdx] = { ...sections[sectionIdx], links };
    update({ sections });
  };

  const removeLink = (sectionIdx: number, linkIdx: number) => {
    const sections = [...data.sections];
    sections[sectionIdx] = {
      ...sections[sectionIdx],
      links: sections[sectionIdx].links.filter((_, i) => i !== linkIdx),
    };
    update({ sections });
  };

  return (
    <div className="space-y-4">
      {/* Global title & description */}
      <div className="space-y-2">
        <Label>
          Titre <span className="text-destructive ml-1">*</span>
        </Label>
        <Input
          value={data.title}
          disabled={disabled}
          placeholder="Titre des ressources"
          onChange={(e) => update({ title: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>
          Description <span className="text-destructive ml-1">*</span>
        </Label>
        <Textarea
          value={data.description}
          disabled={disabled}
          placeholder="Description des ressources"
          rows={3}
          onChange={(e) => update({ description: e.target.value })}
        />
      </div>

      {/* Sections */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Sections</Label>
          {!disabled && (
            <Button variant="outline" size="sm" onClick={addSection}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              Ajouter une section
            </Button>
          )}
        </div>

        {data.sections.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">Aucune section</p>
        )}

        {data.sections.map((section, sIdx) => {
          const collapsed = !expandedSections.has(sIdx);
          return (
            <Card key={sIdx} className="border-border/50">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleSection(sIdx)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  <Input
                    value={section.title}
                    disabled={disabled}
                    placeholder="Section sans titre"
                    className="h-7 text-sm font-medium flex-1 border-transparent hover:border-input focus:border-input bg-transparent"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{section.links.length} lien(s)</span>
                  {!disabled && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => removeSection(sIdx)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </CardHeader>

              {!collapsed && (
                <CardContent className="px-4 pb-4 pt-0 space-y-3">
                  <Input
                    value={section.tooltip || ""}
                    disabled={disabled}
                    placeholder="Texte d'infobulle (facultatif)"
                    className="h-8 text-sm"
                    onChange={(e) => updateSection(sIdx, { tooltip: e.target.value || undefined })}
                  />

                  {/* Links */}
                  <div className="space-y-2 pl-3 border-l-2 border-border/50">
                    {section.links.map((link, lIdx) => (
                      <div key={lIdx} className="bg-muted/30 rounded-md p-3 relative">
                        {!disabled && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 absolute top-2 right-2 text-destructive hover:text-destructive"
                            onClick={() => removeLink(sIdx, lIdx)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                        <div className="space-y-1.5 pr-8">
                          <Input
                            value={link.title}
                            disabled={disabled}
                            placeholder="Titre du lien"
                            className="h-8 text-sm"
                            onChange={(e) => updateLink(sIdx, lIdx, { title: e.target.value })}
                          />
                          <Input
                            value={link.description}
                            disabled={disabled}
                            placeholder="Description"
                            className="h-8 text-sm"
                            onChange={(e) => updateLink(sIdx, lIdx, { description: e.target.value })}
                          />
                          <Input
                            value={link.url}
                            disabled={disabled}
                            placeholder="https://..."
                            className="h-8 text-sm"
                            onChange={(e) => updateLink(sIdx, lIdx, { url: e.target.value })}
                          />
                        </div>
                      </div>
                    ))}
                    {!disabled && (
                      <Button variant="ghost" size="sm" className="w-full" onClick={() => addLink(sIdx)}>
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Ajouter un lien
                      </Button>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
